<template>
  <div class="asset-notification-history-wrapper">
    <div class="notification-history-container">
      <div class="row mb-4">
        <div class="col-12">
          <h3 class="dashboard-title">
            <i class="bi bi-bell-fill" />
            {{ $t('components.assetNotificationHistory.title') }}
          </h3>
        </div>
      </div>

      <DxDataGrid
        :ref="gridRef"
        :data-source="store"
        :hover-state-enabled="true"
        :remote-operations="true"
        :column-auto-width="false"
        :show-borders="true"
        :scrolling="scrollingConfig"
        :column-hiding-enabled="true"
        :word-wrap-enabled="true"
        @optionChanged="detectFilters"
      >
        <DxHeaderFilter :visible="true" />
        <DxFilterRow :visible="true" />
        <DxPaging :page-size="20" />
        <DxPager
          :allowed-page-sizes="[10, 20, 50, 100]"
          :show-info="true"
          :show-navigation-buttons="true"
          :show-page-size-selector="true"
          :visible="true"
        />

        <DxColumn
          data-field="message"
          :caption="$t('components.assetNotificationHistory.message')"
          cell-template="message-template"
          :min-width="isMobile ? 200 : 300"
          :hiding-priority="3"
        />

        <DxColumn
          data-field="asset.name"
          :caption="$t('components.assetNotificationHistory.asset')"
          cell-template="asset-template"
          :min-width="isMobile ? 150 : 200"
          :hiding-priority="1"
        />

        <DxColumn
          data-field="user.name"
          :caption="$t('components.assetNotificationHistory.recipient')"
          cell-template="recipient-template"
          :calculate-filter-expression="recipientFilterExpression"
          :min-width="isMobile ? 160 : 220"
          :hiding-priority="2"
        />

        <DxColumn
          data-field="user.assetGroups"
          :caption="$t('components.assetNotificationHistory.groups')"
          cell-template="groups-template"
          :calculate-filter-expression="groupsFilterExpression"
          :min-width="isMobile ? 180 : 260"
          :hiding-priority="3"
        />

        <DxColumn
          data-field="sent_at"
          :caption="$t('components.assetNotificationHistory.sentAt')"
          cell-template="sent-at-template"
          alignment="center"
          :width="isMobile ? 120 : 180"
          :allow-filtering="false"
          :hiding-priority="0"
        />

        <DxColumn
          :caption="$t('components.assetNotificationHistory.channels')"
          cell-template="channels-template"
          :width="isMobile ? 100 : 150"
          :allow-filtering="false"
          alignment="center"
          :hiding-priority="0"
        />

        <DxColumn
          :caption="$t('components.assetNotificationHistory.status')"
          cell-template="status-template"
          :width="isMobile ? 80 : 120"
          :allow-filtering="false"
          alignment="center"
          :hiding-priority="4"
        />

        <template #message-template="{ data }">
          <div class="message-cell">
            <div class="message-content">
              {{ formatMessage(data.data.message) }}
            </div>
          </div>
        </template>

        <template #asset-template="{ data }">
          <div class="asset-cell">
            <div class="asset-badge">
              <i class="bi bi-broadcast-pin" />
              <span>{{ getAssetLabel(data.data.message, data.data.asset) }}</span>
            </div>
          </div>
        </template>

        <template #sent-at-template="{ data }">
          <div class="sent-at-cell">
            <div class="date-time">
              {{ formatDateTime(data.data.sent_at) }}
            </div>
          </div>
        </template>

        <template #recipient-template="{ data }">
          <div class="recipient-cell">
            {{ getRecipientName(data.data.user) }}
          </div>
        </template>

        <template #groups-template="{ data }">
          <div class="groups-cell">
            {{ getRecipientGroups(data.data.user) }}
          </div>
        </template>

        <template #channels-template="{ data }">
          <div class="channels-cell">
            <div class="channel-icons">
              <i
                v-if="data.data.is_sent_by_email"
                class="bi bi-envelope-fill channel-icon email-icon"
                :title="getEmailTooltip(data.data.email)"
              />
              <i
                v-if="data.data.is_sent_by_sms"
                class="bi bi-phone-fill channel-icon sms-icon"
                :title="getPhoneTooltip(data.data.phone_number)"
              />
              <i
                v-if="data.data.is_sent_by_mercure"
                class="bi bi-broadcast channel-icon mercure-icon"
                :title="getMercureTooltip()"
              />
            </div>
          </div>
        </template>

        <template #status-template="{ data }">
          <div class="status-cell">
            <div
              class="status-badge"
              :class="data.data.is_read ? 'read' : 'unread'"
            >
              <i :class="data.data.is_read ? 'bi bi-check-circle-fill' : 'bi bi-circle-fill'" />
            </div>
          </div>
        </template>
      </DxDataGrid>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  DxDataGrid,
  DxColumn,
  DxFilterRow,
  DxHeaderFilter,
  DxPaging,
  DxPager,
} from 'devextreme-vue/data-grid'
import CustomStore from 'devextreme/data/custom_store'
import { getAssetNotificationHistoryList } from '@/crud/notifcations_history/getters'
import { getHumanDateTimeFromDateTime } from '@/utils/tools'
import i18n from '@/i18n'

function getAssetParamKeyMatches(term: string): string[] {
  const normalized = term.trim().toLowerCase()
  if (!normalized) return []

  const locale = i18n.global?.locale?.value || i18n.global?.locale || 'pl'
  const messages = (i18n.global as any)?.messages?.value || (i18n.global as any)?.messages
  const assetParams = messages?.[locale]?.asset_params || {}

  return Object.entries(assetParams)
    .filter(([, value]) => typeof value === 'string')
    .filter(([, value]) => (value as string).toLowerCase().includes(normalized))
    .map(([key]) => key)
}

function getMessageTranslateKeyMatches(term: string): string[] {
  const normalized = term.trim().toLowerCase()
  if (!normalized) return []

  const locale = i18n.global?.locale?.value || i18n.global?.locale || 'pl'
  const messages = (i18n.global as any)?.messages?.value || (i18n.global as any)?.messages
  const notificationMessages = messages?.[locale]?.components?.notificationsHistory?.messages

  if (!notificationMessages || typeof notificationMessages !== 'object') {
    return []
  }

  const matches: string[] = []

  const walk = (node: any, pathParts: string[]) => {
    if (!node || typeof node !== 'object') return
    for (const [key, value] of Object.entries(node)) {
      const nextPath = [...pathParts, key]
      if (typeof value === 'string') {
        if (value.toLowerCase().includes(normalized)) {
          matches.push(nextPath.join('.'))
        }
      } else if (value && typeof value === 'object') {
        walk(value, nextPath)
      }
    }
  }

  walk(notificationMessages, [])
  return matches
}

function normalizeFilter(filter: any) {
  if (!filter) return null

  const result: Record<string, string | string[]> = {}

  const addValue = (field: string, value: any) => {
    if (value === undefined || value === null) return
    const normalized = String(value).trim()
    if (!normalized) return
    if (field === 'message') {
      const matches = getAssetParamKeyMatches(normalized)
      const translateMatches = getMessageTranslateKeyMatches(normalized)
      if (matches.length || translateMatches.length) {
        result[field] = Array.from(new Set([normalized, ...matches, ...translateMatches]))
        return
      }
    }
    result[field] = normalized
  }

  const walk = (node: any) => {
    if (!Array.isArray(node)) return

    if (
      node.length === 3 &&
      typeof node[0] === 'string' &&
      typeof node[1] === 'string'
    ) {
      const [field, operator, value] = node
      if (['contains', '=', 'startswith', 'endswith'].includes(operator)) {
        addValue(field, value)
      }
      return
    }

    for (const item of node) {
      if (Array.isArray(item)) {
        walk(item)
      }
    }
  }

  walk(filter)

  return Object.keys(result).length ? result : null
}

function getStore() {
  return new CustomStore({
    cacheRawData: false,
    key: 'id',
    load(loadOptions) {
      return new Promise((resolve, reject) => {
        const skip = loadOptions.skip || 0
        const take = loadOptions.take || 20
        const sort = loadOptions.sort || [{ selector: 'sent_at', desc: true }]
        const filter = normalizeFilter(loadOptions.filter)

        getAssetNotificationHistoryList({
          skip,
          take,
          sort,
          filter
        })
          .then((response: any) => {
            const data = response.data || []
            const totalCount = response.totalCount || 0

            resolve({
              data: data,
              totalCount: totalCount
            })
          })
          .catch((error) => {
            console.error('Asset Notification History API load error:', error)
            reject(error)
          })
      })
    }
  })
}

export default defineComponent({
  name: 'AssetNotificationHistoryView',
  components: {
    DxDataGrid,
    DxColumn,
    DxFilterRow,
    DxHeaderFilter,
    DxPaging,
    DxPager,
  },
  setup(props, context) {
    const i18n = useI18n()
    context.emit('pageName', i18n.t('aside.menu.pages.assetNotificationHistory'))

    watch(() => i18n.locale.value, () => {
      context.emit('pageName', i18n.t('aside.menu.pages.assetNotificationHistory'))
    })
  },
  data(): {
    store: any,
    gridRef: string,
    hasFilters: boolean,
    windowWidth: number
  } {
    return {
      store: getStore(),
      gridRef: 'assetNotificationHistoryGrid',
      hasFilters: false,
      windowWidth: window.innerWidth
    }
  },
  computed: {
    grid() {
      // @ts-ignore
      return this.$refs[this.gridRef]?.instance
    },
    isMobile() {
      return this.windowWidth <= 767
    },
    scrollingConfig() {
      return {
        mode: 'standard',
        rowRenderingMode: 'standard',
        columnRenderingMode: 'virtual',
        useNative: true,
        scrollByContent: true,
        scrollByThumb: true,
        showScrollbar: 'onScroll',
      }
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth
    },
    detectFilters() {
      this.hasFilters = !!this.grid?.getCombinedFilter()
    },
    formatMessage(message: string | null) {
      if (!message) return '—'

      try {
        const parsed = JSON.parse(message)
        if (parsed && typeof parsed === 'object') {
          if (parsed.content) {
            return parsed.content
          }

          const translationKey = parsed.translateKey
            ? `components.notificationsHistory.messages.${parsed.translateKey}`
            : null

          const rawValues = parsed.translateValues && typeof parsed.translateValues === 'object'
            ? parsed.translateValues
            : {}

          const translationValues: Record<string, any> = {}
          for (const [key, value] of Object.entries(rawValues)) {
            translationValues[key.replace(/%/g, '')] = value
          }

          if (translationValues.paramKey) {
            const paramKeyTranslation = this.$t(`asset_params.${translationValues.paramKey}`)
            if (paramKeyTranslation && !paramKeyTranslation.startsWith('asset_params.')) {
              translationValues.paramKey = paramKeyTranslation
            }
          }

          if (parsed.translateKey === 'notifications_content.asset_param_binary' && translationValues.value) {
            const statusTranslation = this.$t(`asset_params.binary_status.${translationValues.value}`)
            if (statusTranslation && !statusTranslation.startsWith('asset_params.')) {
              translationValues.value = statusTranslation
            }
          }

          if (translationValues.assetLabel) {
            translationValues.assetLabel = ''
          }

          if (translationKey && this.$te(translationKey)) {
            return this.$t(translationKey, translationValues)
          }
        }

        return message
      } catch (e) {
        // Not JSON, return as is
        return message
      }
    },
    getAssetLabel(message: string | null, asset: any) {
      if (!message) return this.getAssetName(asset)

      try {
        const parsed = JSON.parse(message)
        const rawValues = parsed && typeof parsed === 'object'
          ? parsed.translateValues
          : null

        if (rawValues && typeof rawValues === 'object') {
          const assetLabel = rawValues['%assetLabel%'] ?? rawValues['assetLabel']
          if (assetLabel) {
            return assetLabel
          }
        }
      } catch (e) {
        // ignore, fall back to asset info
      }

      return this.getAssetName(asset)
    },
    getEmailTooltip(email: string | null) {
      if (!email) return this.$t('components.assetNotificationHistory.channelEmail')
      return this.$t('components.assetNotificationHistory.channelEmailValue', { value: email })
    },
    getPhoneTooltip(phone: string | null) {
      if (!phone) return this.$t('components.assetNotificationHistory.channelSms')
      return this.$t('components.assetNotificationHistory.channelSmsValue', { value: phone })
    },
    getMercureTooltip() {
      return this.$t('components.assetNotificationHistory.channelMercure')
    },
    getRecipientName(user: any) {
      if (!user) return '—'
      const name = user.name || ''
      const surname = user.surname || ''
      const fullName = `${name} ${surname}`.trim()
      return fullName || '—'
    },
    getRecipientGroups(user: any) {
      const groups = user?.assetGroups
      if (!Array.isArray(groups) || groups.length === 0) return '—'
      return groups
        .map((group: any) => group?.name)
        .filter((name: string | null | undefined) => !!name)
        .join(', ')
    },
    recipientFilterExpression(value: string) {
      if (!value) return null
      return ['user.name', 'contains', value]
    },
    groupsFilterExpression(value: string) {
      if (!value) return null
      return ['user.assetGroups', 'contains', value]
    },
    getAssetName(asset: any) {
      if (!asset) return '—'
      return asset.name || asset.serial_number || `Asset #${asset.id}`
    },
    formatDateTime(dateTime: string | null) {
      if (!dateTime) return '—'
      return getHumanDateTimeFromDateTime(dateTime)
    },
  }
})
</script>

<style lang="scss" scoped>
@import '@/assets/scss/asset-notification-history-view.scss';
</style>
